/**
 * skill-debug-events — Event bus for the Skill Debug HUD.
 *
 * useSkillStream + useV4InvalidateOnStream + sseStore emit events here;
 * SkillDebugHUD subscribes.
 *
 * Discriminated union on `type`: the HUD narrows `event.data` via the
 * discriminator instead of defensive `'field' in data` checks.
 *
 * Wire-event data shapes come from the Zod schemas in
 * skill-stream-schemas.ts (validated at the trust boundary in
 * useSkillStream). Frontend-only events are typed here directly.
 *
 * Only active when import.meta.env.DEV is true (callers gate on that).
 */

import type {
  CompletePayload,
  DeltaPayload,
  ErrorPayload,
  ToolEndPayload,
  ToolStartPayload,
} from '@shared/schemas/skill-stream-events';

export type SkillDebugEventType = SkillDebugEventBody['type'];

type DebugEventSource = 'skill' | 'app_home';

/**
 * Frontend-emitted event payloads. These don't cross a trust
 * boundary (they're emitted by already-trusted frontend code), so
 * they're typed here directly rather than schema-validated.
 */
interface DraftUpdateData {
  field: string;
  previousValue: unknown;
  newValue: unknown;
  tool: string;
}

interface TimingData {
  totalMs: number;
  segments: Array<{
    phase: 'thinking' | 'tool' | 'response';
    startMs: number;
    endMs: number;
  }>;
  tools: string[];
}

interface ConnectionOpenData {
  status: number;
}

type ConnectionCloseData = Record<string, never>;

interface ConnectionErrorData {
  message: string;
}

/**
 * Four-variant discriminated union on `trigger`. Emitted in
 * useV4InvalidateOnStream in response to app-home SSE events.
 */
type CacheInvalidateData =
  | {
      trigger: 'artifact:updated:deliveryStatus';
      uuid: string;
      deliveryStatus: string;
      action: 'invalidate' | 'remove';
    }
  | {
      trigger: 'artifact:updated';
      uuid: string;
      fields: string[];
      action: 'patch';
    }
  | {
      trigger: 'artifact:deleted';
      uuid: string;
      action: 'remove';
    }
  | {
      trigger: 'invalidate';
      reason: string;
      scope: 'granular' | 'full';
      action: 'invalidate';
    };

/**
 * Meta-envelope for the app-home SSE stream. `event` is the
 * discriminator; `payload` carries the source event's validated
 * payload (already validated upstream in sseStore). Kept loose here
 * because the HUD renders generically for debug purposes and the
 * source shapes are many.
 */
interface AppHomeSseData {
  event: string;
  [key: string]: unknown;
}

/**
 * Discriminated union. The HUD renders each case typed, without
 * needing defensive runtime-shape checks.
 */
export type SkillDebugEventBody =
  | { type: 'delta'; data: DeltaPayload | null }
  | { type: 'tool_start'; data: ToolStartPayload | null }
  | { type: 'tool_end'; data: ToolEndPayload | null }
  | { type: 'complete'; data: CompletePayload | null }
  | { type: 'error'; data: ErrorPayload | null }
  | { type: 'draft_update'; data: DraftUpdateData }
  | { type: 'timing'; data: TimingData }
  | { type: 'connection_open'; data: ConnectionOpenData }
  | { type: 'connection_close'; data: ConnectionCloseData }
  | { type: 'connection_error'; data: ConnectionErrorData }
  | { type: 'cache_invalidate'; data: CacheInvalidateData }
  | { type: 'app_home_sse'; data: AppHomeSseData };

export type SkillDebugEvent = {
  timestamp: number;
  /** Which SSE stream this event came from */
  source?: DebugEventSource;
  /** Optional skill identifier(s) — comma-joined IDs (set by useSkillStream) */
  skillSlug?: string;
  /** Optional: last user message at time of event (for error context) */
  lastUserMessage?: string;
} & SkillDebugEventBody;

type Listener = (event: SkillDebugEvent) => void;

const listeners = new Set<Listener>();

export function emitDebugEvent(event: SkillDebugEvent): void {
  for (const cb of listeners) {
    cb(event);
  }
}

/**
 * Subscribe to debug events. Returns an unsubscribe function.
 */
export function subscribeDebugEvents(cb: Listener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
